import type { StoreDetails } from '@api/customer-portal-api';

export interface PortalBanner {
  imageUrl: string;
  link: string | null;
  sizePercent: number | null; // null = full width
}

export interface PortalPerks {
  heading: string | null; // null = default "Your perks"
  lines: string[];
}

export interface StorePortalContent {
  topBanner: PortalBanner | null;
  bottomBanner: PortalBanner | null;
  perks: PortalPerks | null;
}

const MIN_BANNER_SIZE = 40;
const MAX_BANNER_SIZE = 100;

// The backend already validates all of this; the portal only guards against bad data so a
// malformed value hides itself instead of breaking the page.
const nonBlank = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

// A merchant-entered link is rendered as an href, so only http(s) may through ("javascript:" etc.).
const safeLink = (value: unknown): string | null => {
  const link = nonBlank(value);
  if (!link) return null;
  try {
    const { protocol } = new URL(link);
    return protocol === 'http:' || protocol === 'https:' ? link : null;
  } catch {
    return null;
  }
};

const bannerSize = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(MAX_BANNER_SIZE, Math.max(MIN_BANNER_SIZE, Math.round(value)));
};

const bannerOf = (url: unknown, link: unknown, size: unknown): PortalBanner | null => {
  const imageUrl = nonBlank(url);
  if (!imageUrl) return null;
  return { imageUrl, link: safeLink(link), sizePercent: bannerSize(size) };
};

const perksOf = (heading: unknown, perks: unknown): PortalPerks | null => {
  if (!Array.isArray(perks)) return null;
  const lines = perks.map(nonBlank).filter((line): line is string => line !== null);
  if (lines.length === 0) return null;
  return { heading: nonBlank(heading), lines };
};

export const portalContentOf = (
  d: Partial<StoreDetails> | null | undefined,
): StorePortalContent => ({
  topBanner: bannerOf(
    d?.customer_portal_top_banner_url,
    d?.customer_portal_top_banner_link,
    d?.customer_portal_top_banner_size,
  ),
  bottomBanner: bannerOf(
    d?.customer_portal_bottom_banner_url,
    d?.customer_portal_bottom_banner_link,
    d?.customer_portal_bottom_banner_size,
  ),
  perks: perksOf(d?.customer_portal_perks_heading, d?.customer_portal_perks),
});
